/**
 * Hook for managing user files
 *
 * Provides file listing, filtering, and management operations.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { api, FileCategory, FileStatus, UserFileResponse } from '../api';

export interface UseFilesOptions {
  /** Initial category filter */
  category?: FileCategory;
  /** Initial status filter */
  status?: FileStatus;
  /** Initial folder filter (null = root folder only, undefined = all files) */
  folderId?: string | null;
  /** Initial tags filter */
  tags?: string[];
  /** Initial search query */
  search?: string;
  /** Initial sort field */
  sortBy?: 'name' | 'created' | 'updated' | 'accessed' | 'size';
  /** Initial sort order */
  sortOrder?: 'asc' | 'desc';
  /** Page size */
  pageSize?: number;
  /** Auto-fetch on mount */
  autoFetch?: boolean;
}

export interface UseFilesResult {
  /** List of files */
  files: UserFileResponse[];
  /** Total file count matching filters */
  total: number;
  /** Whether files are loading */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
  /** Current page (0-indexed) */
  page: number;
  /** Total pages */
  totalPages: number;
  /** Whether there are more pages */
  hasMore: boolean;
  /** Current filters */
  filters: {
    category?: FileCategory;
    status?: FileStatus;
    folderId?: string | null;
    tags?: string[];
    search?: string;
    sortBy: 'name' | 'created' | 'updated' | 'accessed' | 'size';
    sortOrder: 'asc' | 'desc';
  };
  /** Fetch files with current filters */
  fetchFiles: () => Promise<void>;
  /** Set filter values */
  setFilters: (filters: Partial<UseFilesResult['filters']>) => void;
  /** Go to specific page */
  goToPage: (page: number) => void;
  /** Load next page */
  loadMore: () => Promise<void>;
  /** Get a single file by ID */
  getFile: (fileId: string) => Promise<UserFileResponse>;
  /** Update file metadata */
  updateFile: (
    fileId: string,
    updates: {
      fileName?: string;
      folderId?: string | null;
      tags?: string[];
      category?: FileCategory;
    },
  ) => Promise<UserFileResponse>;
  /** Remove a file from local state (e.g., after server-side cleanup) */
  removeFile: (fileId: string) => void;
  /** Delete a file */
  deleteFile: (fileId: string) => Promise<void>;
  /** Permanently delete a file (removes from DB and S3, cannot be undone) */
  permanentDeleteFile: (fileId: string) => Promise<void>;
  /** Restore a file from Trash to its original folder */
  restoreFromTrash: (fileId: string) => Promise<void>;
  /** Archive a file */
  archiveFile: (fileId: string) => Promise<void>;
  /** Restore an archived file */
  restoreFile: (fileId: string) => Promise<void>;
  /** Get download URL for a file */
  getDownloadUrl: (fileId: string) => Promise<string>;
  /** Perform bulk operations */
  bulkOperation: (
    operation: 'delete' | 'move' | 'tag' | 'restore-from-trash' | 'permanent-delete',
    fileIds: string[],
    params?: {
      folderId?: string;
      tags?: string[];
    },
  ) => Promise<void>;
  /** Refresh the file list */
  refresh: () => Promise<void>;
}

/**
 * Hook for managing user files
 */
export function useFiles(options: UseFilesOptions = {}): UseFilesResult {
  const {
    category: initialCategory,
    status: initialStatus,
    folderId: initialFolderId,
    tags: initialTags,
    search: initialSearch,
    sortBy: initialSortBy = 'created',
    sortOrder: initialSortOrder = 'desc',
    pageSize = 20,
    autoFetch = true,
  } = options;

  const [files, setFiles] = useState<UserFileResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(0);

  const [filters, setFiltersState] = useState({
    category: initialCategory,
    status: initialStatus,
    folderId: initialFolderId,
    tags: initialTags,
    search: initialSearch,
    sortBy: initialSortBy,
    sortOrder: initialSortOrder,
  });

  const totalPages = useMemo(() => Math.ceil(total / pageSize), [total, pageSize]);
  const hasMore = useMemo(() => page < totalPages - 1, [page, totalPages]);

  const fetchFiles = useCallback(
    async (pageNum = page, append = false) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.listFiles({
          category: filters.category,
          status: filters.status,
          folderId: filters.folderId,
          tags: filters.tags,
          search: filters.search,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          limit: pageSize,
          offset: pageNum * pageSize,
        });

        if (append) {
          setFiles((prev) => [...prev, ...response.files]);
        } else {
          setFiles(response.files);
        }
        setTotal(response.total);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch files'));
      } finally {
        setIsLoading(false);
      }
    },
    [filters, pageSize, page],
  );

  const setFilters = useCallback((newFilters: Partial<typeof filters>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
    setPage(0);
    setFiles([]);
  }, []);

  const goToPage = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setPage(newPage);
      }
    },
    [totalPages],
  );

  const loadMore = useCallback(async () => {
    if (hasMore && !isLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      await fetchFiles(nextPage, true);
    }
  }, [hasMore, isLoading, page, fetchFiles]);

  const getFile = useCallback(async (fileId: string): Promise<UserFileResponse> => {
    const response = await api.getFile(fileId);
    return response.file;
  }, []);

  const updateFile = useCallback(
    async (
      fileId: string,
      updates: {
        fileName?: string;
        folderId?: string | null;
        tags?: string[];
        category?: FileCategory;
      },
    ): Promise<UserFileResponse> => {
      const response = await api.updateFile(fileId, updates);

      // Update local state - if folderId changed and no longer matches filter, remove it
      setFiles((prev) => {
        // Check if file was moved to a different folder
        if (updates.folderId !== undefined) {
          const movedToNewFolder = updates.folderId !== filters.folderId;
          if (movedToNewFolder) {
            // Remove the file from local state since it's no longer in this folder
            setTotal((t) => t - 1);
            return prev.filter((f) => f.fileId !== fileId);
          }
        }
        // Otherwise just update the file in place
        return prev.map((f) => (f.fileId === fileId ? response.file : f));
      });

      return response.file;
    },
    [filters.folderId],
  );

  const removeFile = useCallback((fileId: string): void => {
    setFiles((prev) => prev.filter((f) => f.fileId !== fileId));
    setTotal((prev) => prev - 1);
  }, []);

  const deleteFile = useCallback(async (fileId: string): Promise<void> => {
    await api.deleteFile(fileId);

    // Remove from local state
    setFiles((prev) => prev.filter((f) => f.fileId !== fileId));
    setTotal((prev) => prev - 1);
  }, []);

  const permanentDeleteFile = useCallback(async (fileId: string) => {
    await api.permanentDeleteFile(fileId);
    setFiles((prev) => prev.filter((f) => f.fileId !== fileId));
    setTotal((prev) => prev - 1);
  }, []);

  const restoreFromTrash = useCallback(async (fileId: string) => {
    await api.restoreFromTrash(fileId);
    setFiles((prev) => prev.filter((f) => f.fileId !== fileId));
    setTotal((prev) => prev - 1);
  }, []);

  const archiveFile = useCallback(async (fileId: string): Promise<void> => {
    await api.archiveFile(fileId);

    // Remove from local state (file no longer matches 'active' filter)
    setFiles((prev) => prev.filter((f) => f.fileId !== fileId));
    setTotal((prev) => prev - 1);
  }, []);

  const restoreFile = useCallback(async (fileId: string): Promise<void> => {
    await api.restoreFile(fileId);

    // Remove from local state (file no longer matches 'archived' filter)
    setFiles((prev) => prev.filter((f) => f.fileId !== fileId));
    setTotal((prev) => prev - 1);
  }, []);

  const getDownloadUrl = useCallback(async (fileId: string): Promise<string> => {
    const response = await api.getDownloadUrl(fileId);
    return response.downloadUrl;
  }, []);

  const bulkOperation = useCallback(
    async (
      operation: 'delete' | 'move' | 'tag' | 'restore-from-trash' | 'permanent-delete',
      fileIds: string[],
      params?: { folderId?: string; tags?: string[] },
    ): Promise<void> => {
      await api.bulkFileOperation({
        operation,
        fileIds,
        folderId: params?.folderId,
        tags: params?.tags,
      });

      if (
        operation === 'delete' ||
        operation === 'permanent-delete' ||
        operation === 'restore-from-trash'
      ) {
        // Remove affected files from local state
        setFiles((prev) => prev.filter((f) => !fileIds.includes(f.fileId)));
        setTotal((prev) => prev - fileIds.length);
      } else {
        // Refresh to get updated data
        await fetchFiles(0, false);
        setPage(0);
      }
    },
    [fetchFiles],
  );

  const refresh = useCallback(async () => {
    setPage(0);
    await fetchFiles(0, false);
  }, [fetchFiles]);

  // Sync folderId filter when prop changes
  useEffect(() => {
    setFiltersState((prev) => {
      if (prev.folderId !== initialFolderId) {
        return { ...prev, folderId: initialFolderId };
      }
      return prev;
    });
  }, [initialFolderId]);

  // Auto-fetch on mount and when filters change
  useEffect(() => {
    if (autoFetch) {
      fetchFiles(0, false);
    }
  }, [filters, autoFetch, fetchFiles]);

  return {
    files,
    total,
    isLoading,
    error,
    page,
    totalPages,
    hasMore,
    filters,
    fetchFiles: () => fetchFiles(page, false),
    setFilters,
    goToPage,
    loadMore,
    getFile,
    updateFile,
    removeFile,
    deleteFile,
    permanentDeleteFile,
    restoreFromTrash,
    archiveFile,
    restoreFile,
    getDownloadUrl,
    bulkOperation,
    refresh,
  };
}

export default useFiles;
